import { Input } from "@components/Input";
import { UpIcon } from "@components/Icons/Up";
import { deleteField, doc, setDoc, Timestamp } from "@firebase/firestore";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { FC, useContext, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { FirebaseContext } from "src/helpers/firebase";
import { useConfetti } from "src/helpers/useConfetti";
import { useTeam } from "src/helpers/useTeam";
import { useToast } from "src/helpers/useToast";
import { CheckIcon } from "@components/Icons/Check";

type Props = {
  challenge: VotingChallenge;
  solution?: VotingSolution;
  teamId: string;
  eventId: string;
};

export const Voting: FC<Props> = (props) => {
  const { id: challengeId, entries = [] } = props.challenge;
  const { eventId, teamId, solution } = props;
  const { t } = useTranslation("challenges");
  const { firestore, functions } = useContext(FirebaseContext);
  const [team] = useTeam();
  const [submitting, setSubmitting] = useState(false);
  const [voting, setVoting] = useState(false);
  useConfetti("submit-button");
  const toast = useToast();

  const { getTranslatedValue } = useTranslatedKey();
  const title = getTranslatedValue(props.challenge, "title");
  const copy = getTranslatedValue(props.challenge, "copy");

  const [addVotingEntry] = useHttpsCallable<
    { eventId: string; challengeId: string; entryName: string },
    { success: boolean; entryId?: string; error?: string }
  >(functions, "addVotingEntryV2");

  const [updateTeamPoints] = useHttpsCallable<API.UpdateTeamPointsRequest, API.UpdateTeamPointsResponse>(
    functions,
    "updateTeamPointsV2",
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<{ entryName: string }>();

  const watchedEntryName = watch("entryName", "");

  // Sort entries alphabetically by entryName
  const sortedEntries = useMemo(() => {
    return [...entries].sort((a, b) => a.entryName.localeCompare(b.entryName));
  }, [entries]);

  // Check if entry name already exists (case-insensitive comparison for validation)
  const entryAlreadyExists = useMemo(() => {
    const trimmedName = watchedEntryName?.trim().toLowerCase() || "";
    if (!trimmedName) return false;
    return entries.some((entry) => entry.entryName.trim().toLowerCase() === trimmedName);
  }, [entries, watchedEntryName]);

  // Check if team has already voted
  const hasVoted = !!solution?.votedForEntryId;
  const votedEntryId = solution?.votedForEntryId;

  const submit = async (formData: { entryName: string }) => {
    if (!team) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(`Team is undefined at challenge submit step. Team: ${team}, event: ${eventId}`);
      return;
    }

    if (entryAlreadyExists) {
      return;
    }

    setSubmitting(true);
    try {
      const result = await addVotingEntry({
        eventId,
        challengeId,
        entryName: formData.entryName.trim(),
      });

      if (result?.data?.success) {
        toast(t("entrySubmitted"), "✅", "success");
        reset();
      } else {
        toast(result?.data?.error || t("problemOccured"), "🤷‍♂️", "error");
      }
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVote = async (entryId: string) => {
    if (!team || voting) return;
    setVoting(true);

    const isVotedFor = votedEntryId === entryId;
    const solutionDocRef = doc(firestore, `events/${eventId}/teams/${teamId}/solutions/${challengeId}`);
    try {
      const isTransferringVote = hasVoted && !isVotedFor;
      if (isTransferringVote) {
        await setDoc(solutionDocRef, { votedForEntryId: entryId }, { merge: true });
        await updateTeamPoints({ teamId, challengeId, eventId, solutionType: "voting", completed: true });
        toast(t("voteSubmitted"), "👍", "success");
        return;
      }

      const isRemovingVote = hasVoted && isVotedFor;
      if (isRemovingVote) {
        // Remove the vote - set completed to false and clear votedForEntryId
        await setDoc(solutionDocRef, { votedForEntryId: deleteField() }, { merge: true });
        await updateTeamPoints({ teamId, challengeId, eventId, solutionType: "voting", completed: false });
        return;
      }

      const isAddingVote = !hasVoted;
      if (isAddingVote) {
        await setDoc(solutionDocRef, { votedForEntryId: entryId }, { merge: true });
        await updateTeamPoints({ teamId, challengeId, eventId, solutionType: "voting", completed: true });
        toast(t("voteSubmitted"), "👍", "success");
        return;
      }
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      setVoting(false);
    }
  };

  return (
    <>
      <h1 className="mx-4 mt-16 text-center text-4xl font-semibold">{title}</h1>
      {copy && <p className="mx-4 mt-4 text-center text-base-content/70">{copy}</p>}
      {hasVoted && (
        <p className="mx-4 mt-4 text-center text-lg font-semibold text-base-content/70">{t("voteExists")}</p>
      )}
      <div className="mt-4 flex flex-col items-center">
        <div className="flex w-full max-w-xl flex-col justify-center">
          <div className="mt-12 w-full">
            <h2 className="mb-4 text-center text-xl">{t("currentEntries")} 🗳️</h2>
            <div className="flex w-full flex-col gap-4 px-6">
              {sortedEntries.length === 0 ? (
                <p className="text-center text-base-content/60">{t("noEntriesYet")}</p>
              ) : (
                sortedEntries.map((entry) => {
                  const isVotedFor = votedEntryId === entry.id;

                  return (
                    <div className="w-full" key={entry.id}>
                      <button
                        onClick={() => handleVote(entry.id)}
                        disabled={voting}
                        className={`text-left flex w-full flex-col text-neutral ${voting ? "opacity-50" : ""}`}
                      >
                        <div className={`min-w-2xl flex w-full flex-col rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-3 ${isVotedFor ? "border-violet-500 text-violet-900 font-semibold bg-violet-200 border-2 bg-opacity-30" : ""}`}>
                          <div className="flex w-full items-center">
                            <div className="flex min-w-0 flex-1 flex-col">
                              <p className={`break-words px-4 font-semibold text-neutral ${isVotedFor ? "opacity-100 font-semibold" : "opacity-70"}`}>{entry.entryName}</p>
                            </div>
                            <div className="flex flex-col items-center">
                              <div className={`mr-1`}>
                                <div className="flex justify-center">
                                  {isVotedFor ? (
                                    <CheckIcon className={`h-8 w-8 text-violet-900`} />
                                  ) : (
                                    <UpIcon className={`h-8 w-8 text-neutral opacity-90`} />
                                  )}
                                </div>
                                <div className={`mt-1 text-xs ${isVotedFor ? "text-violet-900 font-semibold" : "text-neutral opacity-90"}`}>{t("yourVote")}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <form onSubmit={handleSubmit(submit)} className="w-full px-6 pt-12">
            <Input
              label={t("addNewEntry")}
              name="entryName"
              placeholder={t("enterEntryName")}
              errors={errors}
              register={register}
              required
              requiredMessage={t("entryNameRequired")}
            />
            {entryAlreadyExists && (
              <p className="mt-1 text-sm text-warning">{t("entryAlreadyExists")}</p>
            )}
            <div className="mb-12 mt-4 flex justify-center">
              <button
                type="submit"
                disabled={submitting || entryAlreadyExists}
                className={`btn-primary btn ${submitting ? "loading" : ""}`}
              >
                {t("addEntry")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
